import { useState, useEffect } from "react";
import { useRuntimeConstants } from "../../../context/runtimeConstants/RuntimeConstantsContext";
import { useFeaturesReady } from "../../../context/featuresReady/FeaturesReadyContext";
import getReturnNotesSummary, {
  ReturnNotesSummary,
} from "../../../api/returnNotesSummary";
import { getFeature } from "../../../utils/features";
import { setProductSummaryTrackingState } from "../../../utils/marketingTracking/productSummaryTrackingState";

type RuntimeConstantsShape = {
  product?: { id?: number | string };
  returnNotesSummary?: { returnNotesSummaryApiTimeout?: number };
  features: {
    returnNotesSummaryEnabled: boolean;
  };
};

type FeaturesReadyShape = {
  areFeaturesReady: boolean;
};

const isValidReturnNotesSummary = (
  data: ReturnNotesSummary | null,
): data is ReturnNotesSummary => {
  const hasValue = (value: string | undefined) =>
    typeof value === "string" && value.trim().length > 0;

  return Boolean(data && hasValue(data.summary) && hasValue(data.disclaimer));
};

const useReturnNotesSummary = (): ReturnNotesSummary | false => {
  const runtimeConstants = useRuntimeConstants() as RuntimeConstantsShape;
  const {
    product,
    returnNotesSummary: summaryConfig,
    features: { returnNotesSummaryEnabled },
  } = runtimeConstants;
  const productId = product?.id;
  const summaryTimeout = summaryConfig?.returnNotesSummaryApiTimeout ?? 3000;

  const [returnNotesSummary, setReturnNotesSummary] =
    useState<ReturnNotesSummary | null>(null);
  const [hasFetchedSummary, setHasFetchedSummary] = useState(false);

  useEffect(() => {
    if (!returnNotesSummaryEnabled) {
      setProductSummaryTrackingState(productId, null);
      return;
    }

    if (hasFetchedSummary) {
      return;
    }

    let isMounted = true;
    let hasSettled = false;

    const completeFetch = (data: ReturnNotesSummary | null) => {
      if (!isMounted || hasSettled) {
        return;
      }

      hasSettled = true;
      clearTimeout(timeout);
      const summary = isValidReturnNotesSummary(data) ? data : null;
      setReturnNotesSummary(summary);
      setHasFetchedSummary(true);
      if (!summary) {
        setProductSummaryTrackingState(productId, null);
      }
    };

    const timeout = setTimeout(() => completeFetch(null), summaryTimeout);

    const fetchReturnNotesSummary = async () => {
      try {
        const data = await getReturnNotesSummary();
        completeFetch(data);
      } catch {
        completeFetch(null);
      }
    };

    fetchReturnNotesSummary();

    return () => {
      isMounted = false;
      clearTimeout(timeout);
      if (!hasSettled) {
        setProductSummaryTrackingState(productId, null);
      }
    };
  }, [hasFetchedSummary, productId, returnNotesSummaryEnabled, summaryTimeout]);

  const { areFeaturesReady } = useFeaturesReady() as FeaturesReadyShape;

  const [isFeatureFlagEnabled, setIsFeatureFlagEnabled] = useState(false);

  useEffect(() => {
    if (!returnNotesSummaryEnabled || !areFeaturesReady || !hasFetchedSummary) {
      return;
    }

    const hasEnabledSummary =
      isValidReturnNotesSummary(returnNotesSummary) &&
      getFeature("pdp-return-notes-summaries").isEnabled();

    setIsFeatureFlagEnabled(hasEnabledSummary);
    setProductSummaryTrackingState(
      productId,
      hasEnabledSummary ? returnNotesSummary : null,
    );
  }, [
    areFeaturesReady,
    hasFetchedSummary,
    productId,
    returnNotesSummary,
    returnNotesSummaryEnabled,
  ]);

  return (
    returnNotesSummaryEnabled &&
    isFeatureFlagEnabled &&
    isValidReturnNotesSummary(returnNotesSummary) &&
    returnNotesSummary
  );
};

export default useReturnNotesSummary;
